/**
 * ClientsPage — Liste paginée des clients
 *
 * Affiche les clients en grille de cartes avec recherche et pagination numérotée.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';
import type { ClientsResponse } from '../types/clients.js';

const LIMIT = 12;

export function ClientsPage() {
  const [data, setData] = useState<ClientsResponse | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchClients = useCallback((currentPage: number, currentSearch: string) => {
    setIsLoading(true);
    setError('');

    const params = new URLSearchParams({
      page: String(currentPage),
      limit: String(LIMIT),
    });
    if (currentSearch.trim()) {
      params.set('search', currentSearch.trim());
    }

    apiFetch<ClientsResponse>(`/clients?${params}`)
      .then((res) => {
        if (res.success && res.data) {
          setData(res.data);
        } else {
          setError(res.error ?? 'Impossible de charger les clients');
        }
      })
      .catch(() => setError('Erreur de connexion au serveur'))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchClients(page, search);
  }, [page, fetchClients]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    fetchClients(1, search);
  }

  function formatDate(dateStr: string): string {
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  }

  const pagination = data?.pagination;

  /** Génère les numéros de page à afficher (avec ellipses) */
  const pageNumbers = useMemo(() => {
    if (!pagination) return [];
    const total = pagination.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages: (number | '...')[] = [1];
    if (page > 3) pages.push('...');

    const start = Math.max(2, page - 1);
    const end = Math.min(total - 1, page + 1);
    for (let i = start; i <= end; i++) pages.push(i);

    if (page < total - 2) pages.push('...');
    pages.push(total);
    return pages;
  }, [pagination, page]);

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 14px 10px 38px',
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    fontSize: '0.95rem',
    color: colors.text,
    background: '#fff',
    outline: 'none',
    fontFamily: 'inherit',
  };

  return (
    <div style={{ maxWidth: 1200, width: '100%' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <i className="bi bi-people" style={{ fontSize: 20, color: colors.accent }} />
            <h1
              style={{
                margin: 0,
                fontSize: '1.75rem',
                fontWeight: 600,
                color: colors.text,
              }}
            >
              Clients
            </h1>
          </div>
          {pagination && (
            <p style={{ margin: 0, fontSize: 14, color: colors.textMid, marginLeft: 30 }}>
              {pagination.total} client{pagination.total > 1 ? 's' : ''}
            </p>
          )}
        </div>
        <Link
          to="/clients/nouveau"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 16px',
            borderRadius: 6,
            background: colors.accent,
            color: '#fff',
            fontSize: '0.9rem',
            fontWeight: 600,
            textDecoration: 'none',
            fontFamily: 'inherit',
          }}
        >
          <i className="bi bi-plus-lg" />
          Nouveau client
        </Link>
      </div>

      {/* Barre de recherche */}
      <form onSubmit={handleSearch} style={{ marginBottom: 24, position: 'relative' }}>
        <i
          className="bi bi-search"
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 14,
            color: colors.textMuted,
          }}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un client (nom, prénom, email, lieu)..."
          style={inputStyle}
          onFocus={(e) => { (e.target as HTMLInputElement).style.borderColor = colors.accent; }}
          onBlur={(e) => { (e.target as HTMLInputElement).style.borderColor = colors.border; }}
        />
      </form>

      {/* Erreur */}
      {error && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 14,
            color: '#991b1b',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <i className="bi bi-exclamation-circle" />
          {error}
        </div>
      )}

      {/* Grille de cartes clients */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: colors.textMuted, fontSize: 14 }}>
          Chargement des clients...
        </div>
      ) : !data || data.clients.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: colors.textMuted }}>
          <i className="bi bi-people" style={{ fontSize: 40, opacity: 0.3, display: 'block', marginBottom: 12 }} />
          <div style={{ fontSize: 14 }}>
            {search ? 'Aucun client trouvé pour cette recherche.' : 'Aucun client enregistré.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {data.clients.map((client) => (
            <Link
              key={client.id}
              to={`/clients/${client.id}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '20px',
                background: '#fff',
                borderRadius: 10,
                border: `1px solid ${colors.border}`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                textDecoration: 'none',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.accent;
                e.currentTarget.style.boxShadow = `0 4px 12px ${colors.accent}22`;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.border;
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Ligne haute : avatar + nom */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: colors.accentLight,
                    color: colors.accentDark,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: 16,
                    flexShrink: 0,
                  }}
                >
                  {client.prenom[0]}{client.nom[0]}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: colors.text }}>
                    {client.prenom} {client.nom}
                  </div>
                  <div style={{ fontSize: 13, color: colors.textMid, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <i className="bi bi-calendar3" style={{ fontSize: 11 }} />
                    {formatDate(client.dateNaissance)}
                    {client.lieuNaissance && (
                      <>
                        <span style={{ margin: '0 4px', color: colors.textLight }}>|</span>
                        <i className="bi bi-geo-alt" style={{ fontSize: 11 }} />
                        {client.lieuNaissance}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Groupes */}
              {client.groupes && client.groupes.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {client.groupes.map((g) => (
                    <span
                      key={g.groupe.id}
                      style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 10,
                        background: '#ede9fe', color: '#6d28d9', fontWeight: 500,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <i className="bi bi-people-fill" style={{ fontSize: 10, marginRight: 3 }} />
                      {g.groupe.nom}{g.role ? ` · ${g.role}` : ''}
                    </span>
                  ))}
                </div>
              )}

              {/* Badges thèmes — bas de la carte */}
              <div style={{ display: 'flex', gap: 4, marginTop: 'auto', paddingTop: 8, borderTop: `1px solid ${colors.border}` }}>
                {client.charts.length === 0 ? (
                  <span style={{
                    fontSize: 11, background: colors.bgPage, color: colors.textMuted,
                    padding: '2px 8px', borderRadius: 10, fontWeight: 500,
                  }}>
                    Aucun thème
                  </span>
                ) : (
                  <>
                    {client.charts.some((c) => c.type === 'NATAL') && (
                      <span style={{
                        fontSize: 11, background: colors.accentLight, color: colors.accentDark,
                        padding: '2px 8px', borderRadius: 10, fontWeight: 500,
                      }}>
                        TN
                      </span>
                    )}
                    {client.charts.filter((c) => c.type === 'REVOLUTION_SOLAIRE').map((c) => (
                      <span key={c.id} style={{
                        fontSize: 11, background: '#e0f2e9', color: '#166534',
                        padding: '2px 8px', borderRadius: 10, fontWeight: 500,
                      }}>
                        RS{c.anneeRs ? ` ${c.anneeRs}` : ''}
                      </span>
                    ))}
                    {client.charts.some((c) => c.type === 'PROGRESSIONS') && (
                      <span style={{
                        fontSize: 11, background: '#ede9fe', color: '#6d28d9',
                        padding: '2px 8px', borderRadius: 10, fontWeight: 500,
                      }}>
                        Prog
                      </span>
                    )}
                    {client.charts.some((c) => c.type === 'TRANSITS') && (
                      <span style={{
                        fontSize: 11, background: '#e0f4fe', color: '#0369a1',
                        padding: '2px 8px', borderRadius: 10, fontWeight: 500,
                      }}>
                        Transits
                      </span>
                    )}
                  </>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination numérotée */}
      {pagination && pagination.totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, paddingTop: 28 }}>
          {/* Précédent */}
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: 6,
              border: `1px solid ${colors.border}`,
              background: '#fff',
              fontSize: 12,
              color: page <= 1 ? '#ddd' : colors.textMid,
              cursor: page <= 1 ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <i className="bi bi-chevron-left" />
          </button>

          {/* Numéros de page */}
          {pageNumbers.map((p, i) =>
            p === '...' ? (
              <span key={`ellipsis-${i}`} style={{ width: 34, textAlign: 'center', fontSize: 13, color: colors.textMuted }}>
                ...
              </span>
            ) : (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 34,
                  height: 34,
                  borderRadius: 6,
                  border: p === page ? `1px solid ${colors.accent}` : `1px solid ${colors.border}`,
                  background: p === page ? colors.accentLight : '#fff',
                  fontSize: 13,
                  fontWeight: p === page ? 600 : 400,
                  color: p === page ? colors.accentDark : colors.textMid,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                }}
              >
                {p}
              </button>
            ),
          )}

          {/* Suivant */}
          <button
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: 6,
              border: `1px solid ${colors.border}`,
              background: '#fff',
              fontSize: 12,
              color: page >= pagination.totalPages ? '#ddd' : colors.textMid,
              cursor: page >= pagination.totalPages ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <i className="bi bi-chevron-right" />
          </button>
        </div>
      )}
    </div>
  );
}
